import React, { useState, useEffect } from 'react';
import { Typography, Card, Button, Spin, Alert, Tag, Divider } from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
  gold: '#D4AF37',
};

const { Title, Paragraph, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;

  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 16px;

  .ant-card-body {
    padding: 24px !important;
  }

  .ant-card-head {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    padding: 12px 24px !important;
    min-height: auto !important;
  }

  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
    font-size: 14px !important;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${COLORS.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled(Text)`
  color: ${COLORS.textSecondary} !important;
  font-size: 13px;
`;

const InfoValue = styled(Text)`
  color: ${COLORS.textPrimary} !important;
  font-size: 13px;
`;

const VerifyButton = styled(Button)`
  background: ${COLORS.accent} !important;
  border-color: ${COLORS.accent} !important;
  color: #0D1117 !important;
  font-weight: 600 !important;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }
`;

type BusinessVerificationStatus = 'PENDING' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

function getStatusTag(status: BusinessVerificationStatus | null | undefined) {
  if (!status || status === 'PENDING') {
    return (
      <Tag icon={<ClockCircleOutlined />} color="default" style={{ fontSize: '12px' }}>
        Not Started
      </Tag>
    );
  }
  if (status === 'SUBMITTED') {
    return (
      <Tag icon={<ExclamationCircleOutlined />} color="processing" style={{ fontSize: '12px' }}>
        Under Review
      </Tag>
    );
  }
  if (status === 'APPROVED') {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: '12px' }}>
        Approved
      </Tag>
    );
  }
  if (status === 'REJECTED') {
    return (
      <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: '12px' }}>
        Rejected
      </Tag>
    );
  }
  return null;
}

function getAlertConfig(status: BusinessVerificationStatus | null | undefined) {
  if (!status || status === 'PENDING') {
    return {
      type: 'info' as const,
      message: 'Business Verification Required',
      description:
        'Your business has not been verified yet. Click the button below to start the KYC Aid business verification process.',
    };
  }
  if (status === 'SUBMITTED') {
    return {
      type: 'warning' as const,
      message: 'Verification Under Review',
      description:
        'Your business verification has been submitted and is currently being reviewed. This usually takes 1–3 business days.',
    };
  }
  if (status === 'APPROVED') {
    return {
      type: 'success' as const,
      message: 'Business Verified',
      description: 'Your business has been successfully verified. No further action is required.',
    };
  }
  if (status === 'REJECTED') {
    return {
      type: 'error' as const,
      message: 'Verification Rejected',
      description:
        'Your business verification was rejected. Please review the rejection reason below and resubmit via the verification link.',
    };
  }
  return null;
}

const MerchantKyc: React.FC = () => {
  const [kycStatus, setKycStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_KYC_STATUS);
      setKycStatus(response.data?.data || response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load KYC status.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartVerification = async () => {
    try {
      setVerifyLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_KYC_VERIFICATION_URL, {
        params: { type: 'BUSINESS' },
      });
      const url = response.data?.data?.verificationUrl || response.data?.verificationUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Could not retrieve verification URL. Please try again or contact support.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to get verification URL.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const businessVerification = kycStatus?.businessVerification;
  const merchantCompany = kycStatus?.merchantCompany;
  const bvStatus: BusinessVerificationStatus =
    merchantCompany?.businessVerificationStatus || businessVerification?.verificationStatus || 'PENDING';

  const canVerify = bvStatus !== 'APPROVED';
  const alertConfig = getAlertConfig(bvStatus);

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Business Verification (KYB)</Title>
        <Paragraph style={{ color: COLORS.textSecondary }}>
          Complete your business identity verification to activate your merchant account.
        </Paragraph>
      </HeaderContainer>

      {loading ? (
        <StyledCard>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        </StyledCard>
      ) : (
        <>
          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}

          {alertConfig && (
            <Alert
              type={alertConfig.type}
              message={alertConfig.message}
              description={alertConfig.description}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <StyledCard
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SafetyCertificateOutlined style={{ color: COLORS.accent }} />
                <span>Verification Status</span>
              </div>
            }
          >
            <StatusRow>
              <Text style={{ color: COLORS.textSecondary, fontSize: 13 }}>Current Status:</Text>
              {getStatusTag(bvStatus)}
            </StatusRow>

            {merchantCompany?.name && (
              <>
                <Divider style={{ borderColor: COLORS.border, margin: '12px 0' }} />
                <InfoRow>
                  <InfoLabel>Business Name</InfoLabel>
                  <InfoValue>{merchantCompany.name}</InfoValue>
                </InfoRow>
              </>
            )}

            {businessVerification?.rejectionReason && bvStatus === 'REJECTED' && (
              <InfoRow>
                <InfoLabel>Rejection Reason</InfoLabel>
                <InfoValue style={{ color: '#ff4d4f', maxWidth: '60%', textAlign: 'right' }}>
                  {businessVerification.rejectionReason}
                </InfoValue>
              </InfoRow>
            )}

            {businessVerification?.submittedAt && (
              <InfoRow>
                <InfoLabel>Submitted At</InfoLabel>
                <InfoValue>
                  {new Date(businessVerification.submittedAt).toLocaleString()}
                </InfoValue>
              </InfoRow>
            )}

            {businessVerification?.approvedAt && bvStatus === 'APPROVED' && (
              <InfoRow>
                <InfoLabel>Approved At</InfoLabel>
                <InfoValue>
                  {new Date(businessVerification.approvedAt).toLocaleString()}
                </InfoValue>
              </InfoRow>
            )}

            <Divider style={{ borderColor: COLORS.border, margin: '16px 0' }} />

            {canVerify && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <VerifyButton
                  icon={<LinkOutlined />}
                  onClick={handleStartVerification}
                  loading={verifyLoading}
                  size="middle"
                >
                  {bvStatus === 'REJECTED' ? 'Resubmit Business Verification' : 'Start Business Verification'}
                </VerifyButton>
                <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                  You will be redirected to the KYC Aid verification portal in a new tab.
                </Text>
              </div>
            )}
          </StyledCard>
        </>
      )}
    </PageContainer>
  );
};

export default MerchantKyc;
